/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { FinancialGateway } from "./FinancialGateway";
import { PaymentGateway } from "./PaymentGateway";
import { AsaasGateway } from "./gateways/AsaasGateway";
import { MercadoPagoGateway } from "./gateways/MercadoPagoGateway";
import { getFinancialConfig } from "./CommissionService";
import { getSystemSettings } from "../../utils/commissionConfig";

export class PaymentGatewayFactory {
  private static asaasInstance: AsaasGateway | null = null;
  private static mercadoPagoInstance: MercadoPagoGateway | null = null;
  private static customGateways: Map<string, FinancialGateway> = new Map();

  /**
   * Registra um novo provedor de gateway financeiro de forma extensível.
   */
  static registerGateway(id: string, gateway: FinancialGateway): void {
    this.customGateways.set(id.toLowerCase(), gateway);
  }

  static registerCustomGateway(id: string, gateway: FinancialGateway): void {
    this.registerGateway(id, gateway);
  }

  /**
   * Obtém a instância do gateway financeiro configurado ou solicitado.
   */
  static getFinancialGateway(gatewayType?: string): FinancialGateway {
    const settings = getSystemSettings();
    const config = getFinancialConfig();

    let targetType = gatewayType || settings.preferredGateway || config.activeGateway || "asaas";
    targetType = String(targetType).toLowerCase();

    if (this.customGateways.has(targetType)) {
      return this.customGateways.get(targetType)!;
    }

    if (targetType === "auto") {
      if (settings.asaasKey && settings.asaasKey.trim().length > 5) {
        targetType = "asaas";
      } else if (settings.mercadoPagoKey && settings.mercadoPagoKey.trim().length > 5) {
        targetType = "mercadopago";
      } else {
        targetType = "asaas";
      }
    }

    if (targetType === "mercadopago" || targetType === "mercado_pago") {
      if (!this.mercadoPagoInstance) {
        this.mercadoPagoInstance = new MercadoPagoGateway();
      }
      return this.mercadoPagoInstance;
    }

    if (!this.asaasInstance) {
      this.asaasInstance = new AsaasGateway();
    }
    return this.asaasInstance;
  }

  /**
   * Limpa gateways customizados (usado para isolamento de testes).
   */
  static clearCustomGateways(): void {
    this.customGateways.clear();
    this.asaasInstance = null;
    this.mercadoPagoInstance = null;
  }

  /**
   * Método de compatibilidade legada.
   */
  static getGateway(gatewayType?: "asaas" | "mercadopago" | "auto" | string): PaymentGateway {
    return this.getFinancialGateway(gatewayType) as any;
  }
}
